import { gql } from 'graphql.macro';
import {
    internalOrderDetailsFragment,
    orderLinkedDentistFragment,
    orderLinkedExtTechFragment,
    orderDetailsFragment,
    deliveryDetailsFragment,
    patientFragment,
} from 'apollo/fragments';

export const certifyUnCertifyOrderTemplate = gql`
    mutation certifyUnCertifyOrder($orderId: MongoDbObjectId!) {
        certifyUnCertifyOrder(orderId: $orderId) {
            _id
            isCertified
        }
    }
`;

export const deleteDraftOrderTemplate = gql`
    mutation deleteDraftOrder($_id: MongoDbObjectId!) {
        deleteDraftOrder(_id: $_id)
    }
`;

export const toggleLabFlagTemplate = gql`
    mutation toggleLabFlag($_id: MongoDbObjectId!, $flagValue: Boolean!) {
        toggleLabFlag(_id: $_id, flagValue: $flagValue) {
            _id
            labFlag
        }
    }
`;

export const toggleTruckIconTemplate = gql`
    mutation toggleTruckIcon($_id: MongoDbObjectId!, $truckValue: Boolean!, $isLabHaveOwnResource: Boolean!) {
        toggleTruckIcon(_id: $_id, truckValue: $truckValue, isLabHaveOwnResource: $isLabHaveOwnResource) {
            _id
            deliveryDetails {
                isLabHandleRequest
                isLabHaveOwnResource
            }
        }
    }
`;

export const notesTemplate = gql`
    mutation notes($labId: MongoDbObjectId!, $orderId: MongoDbObjectId!, $notes: String!) {
        notes(labId: $labId, orderId: $orderId, notes: $notes) {
            ...internalOrderDetailsFields
        }
    }
    ${internalOrderDetailsFragment}
`;

export const deleteInternalFileTemplate = gql`
    mutation deleteInternalFile($fileId: MongoDbObjectId!, $orderId: MongoDbObjectId!) {
        deleteInternalFile(fileId: $fileId, orderId: $orderId) {
            ...internalOrderDetailsFields
        }
    }
    ${internalOrderDetailsFragment}
`;

export const addLinkedDentistTemplate = gql`
    mutation addLinkedDentist($orderId: MongoDbObjectId!, $userId: MongoDbObjectId!) {
        addLinkedDentist(orderId: $orderId, userId: $userId) {
            ...orderLinkedDentistFields
        }
    }
    ${orderLinkedDentistFragment}
`;

export const addLinkedExtTechTemplate = gql`
    mutation addLinkedExtTech($orderId: MongoDbObjectId!, $userId: MongoDbObjectId!) {
        addLinkedExtTech(orderId: $orderId, userId: $userId) {
            ...orderLinkedExtTechFields
        }
    }
    ${orderLinkedExtTechFragment}
`;

export const removeLinkedDentistTemplate = gql`
    mutation removeLinkedDentist($orderId: MongoDbObjectId!, $userId: MongoDbObjectId!) {
        removeLinkedDentist(orderId: $orderId, userId: $userId) {
            ...orderLinkedDentistFields
        }
    }
    ${orderLinkedDentistFragment}
`;

export const removeLinkedExtTechTemplate = gql`
    mutation removeLinkedExtTech($orderId: MongoDbObjectId!, $userId: MongoDbObjectId!) {
        removeLinkedExtTech(orderId: $orderId, userId: $userId) {
            ...orderLinkedExtTechFields
        }
    }
    ${orderLinkedExtTechFragment}
`;

export const acceptOrderTemplate = gql`
    mutation acceptOrder(
        $orderId: MongoDbObjectId!
        $sample: SampleInput!
        $date: Date
        $internalDeliveryDate: Date
        $externalDeliveryDate: Date
        $productionPlan: [ProductionPlanStepInput!]
        $panNumber: String
        $panNumberDescription: String
    ) {
        acceptOrder(
            orderId: $orderId
            sample: $sample
            date: $date
            internalDeliveryDate: $internalDeliveryDate
            externalDeliveryDate: $externalDeliveryDate
            productionPlan: $productionPlan
            panNumber: $panNumber
            panNumberDescription: $panNumberDescription
        ) {
            ...orderDetailsFields
        }
    }
    ${orderDetailsFragment}
`;

export const sendOrderTemplate = gql`
    mutation sendOrder(
        $orderId: MongoDbObjectId!
        $approvedBy: ApprovedByInput!
        $date: Date
        $isProductionFinished: Boolean!
        $invoiceAction: InvoiceAction!
        $isArchive: Boolean
        $invoiceNumber: String
        $productionValueReports: [ProductionValueReportInput]
    ) {
        sendOrder(
            orderId: $orderId
            approvedBy: $approvedBy
            date: $date
            isProductionFinished: $isProductionFinished
            invoiceAction: $invoiceAction
            isArchive: $isArchive
            invoiceNumber: $invoiceNumber
            productionValueReports: $productionValueReports
        ) {
            ...orderDetailsFields
        }
    }
    ${orderDetailsFragment}
`;

// DD-3391: the elevated external tier's own send. No invoice number and no
// per-order invoice choice - creating an invoice is not theirs to do.
export const sendOrderAsExternalUserTemplate = gql`
    mutation sendOrderAsExternalUser(
        $orderId: MongoDbObjectId!
        $approvedBy: ApprovedByInput!
        $date: Date
        $isProductionFinished: Boolean!
        $invoiceAction: ExternalUserInvoiceAction!
        $isArchive: Boolean
        $productionValueReports: [ProductionValueReportInput]
    ) {
        sendOrderAsExternalUser(
            orderId: $orderId
            approvedBy: $approvedBy
            date: $date
            isProductionFinished: $isProductionFinished
            invoiceAction: $invoiceAction
            isArchive: $isArchive
            productionValueReports: $productionValueReports
        ) {
            ...orderDetailsFields
        }
    }
    ${orderDetailsFragment}
`;

export const labWorkStartedOrderTemplate = gql`
    mutation labWorkStartedOrder($orderId: MongoDbObjectId!, $status: String!) {
        labWorkStartedOrder(orderId: $orderId, status: $status) {
            ...orderDetailsFields
        }
    }
    ${orderDetailsFragment}
`;

export const createWarrantyOrderTemplate = gql`
    mutation createWarrantyOrder($orderId: MongoDbObjectId!, $warrantyType: String!, $orderSection: OrderSection!) {
        createWarrantyOrder(orderId: $orderId, warrantyType: $warrantyType, orderSection: $orderSection) {
            _id
            isCreatedByLab
            type
        }
    }
`;

export const saveInvoiceNumberTemplate = gql`
    mutation saveInvoiceNumber($orderId: MongoDbObjectId!, $invoiceNumber: String!) {
        saveInvoiceNumber(orderId: $orderId, invoiceNumber: $invoiceNumber) {
            _id
            tempInvoiceNumber
        }
    }
`;

export const saveInternalOrderIdTemplate = gql`
    mutation saveInternalOrderId($orderId: MongoDbObjectId!, $internalOrderId: String!) {
        saveInternalOrderId(orderId: $orderId, internalOrderId: $internalOrderId) {
            _id
            internalOrderId
        }
    }
`;

export const updateOrderPanNumberTemplate = gql`
    mutation updateOrderPanNumber($orderId: MongoDbObjectId!, $panNumber: String!, $panNumberDescription: String) {
        updateOrderPanNumber(orderId: $orderId, panNumber: $panNumber, panNumberDescription: $panNumberDescription) {
            _id
            panNumber
            panNumberDescription
        }
    }
`;

export const markOrderProductionFinishedTemplate = gql`
    mutation markOrderProductionFinished(
        $orderId: MongoDbObjectId!
        $productionValueReports: [ProductionValueReportInput]
    ) {
        markOrderProductionFinished(orderId: $orderId, productionValueReports: $productionValueReports) {
            _id
            isProductionFinished
        }
    }
`;

export const markOrderProductionNotFinishedTemplate = gql`
    mutation markOrderProductionNotFinished($orderId: MongoDbObjectId!) {
        markOrderProductionNotFinished(orderId: $orderId) {
            _id
            isProductionFinished
            deliveryNotePdfUrl
        }
    }
`;

export const approveOrderTemplate = gql`
    mutation approveOrder(
        $orderId: MongoDbObjectId!
        $isPriceConfirmed: Boolean!
        $rating: RatingInput!
        $isArchive: Boolean
    ) {
        approveOrder(orderId: $orderId, isPriceConfirmed: $isPriceConfirmed, rating: $rating, isArchive: $isArchive) {
            ...orderDetailsFields
        }
    }
    ${orderDetailsFragment}
`;

export const archiveOrderTemplate = gql`
    mutation archiveOrder($orderId: MongoDbObjectId!) {
        archiveOrder(orderId: $orderId) {
            ...orderDetailsFields
        }
    }
    ${orderDetailsFragment}
`;

export const unArchiveOrderTemplate = gql`
    mutation unArchiveOrder($orderId: MongoDbObjectId!) {
        unArchiveOrder(orderId: $orderId) {
            ...orderDetailsFields
        }
    }
    ${orderDetailsFragment}
`;

export const declineDialogTemplate = gql`
    mutation declineOrder($declineMessage: String!, $orderId: MongoDbObjectId!, $cancelSubOrders: Boolean) {
        declineOrder(declineMessage: $declineMessage, orderId: $orderId, cancelSubOrders: $cancelSubOrders) {
            ...orderDetailsFields
        }
    }
    ${orderDetailsFragment}
`;

export const cancelOrderTemplate = gql`
    mutation cancelOrder($cancelMessage: String!, $orderId: MongoDbObjectId!) {
        cancelOrder(cancelMessage: $cancelMessage, orderId: $orderId) {
            ...orderDetailsFields
        }
    }
    ${orderDetailsFragment}
`;

export const rateOrderTemplate = gql`
    mutation rateOrder($orderId: MongoDbObjectId!, $rating: RatingInput!) {
        rateOrder(orderId: $orderId, rating: $rating) {
            ...orderDetailsFields
        }
    }
    ${orderDetailsFragment}
`;

export const sampleAndPackageTemplate = gql`
    mutation sampleAndPackage($orderId: MongoDbObjectId!, $sample: SampleInput!) {
        sampleAndPackage(orderId: $orderId, sample: $sample) {
            _id
            deliveryDetails {
                ...deliveryDetailsFields
            }
            version
            availableVersions {
                version
                createdAt
            }
        }
    }
    ${deliveryDetailsFragment}
`;

export const trackingLinkTemplate = gql`
    mutation trackingLink($orderId: MongoDbObjectId!, $transportTrackingLink: String!) {
        trackingLink(orderId: $orderId, transportTrackingLink: $transportTrackingLink) {
            _id
            transportTrackingLink
        }
    }
`;

export const patientInfoTemplate = gql`
    mutation patientInfo($orderId: MongoDbObjectId!, $patient: PatientInfoInput!) {
        patientInfo(orderId: $orderId, patient: $patient) {
            _id
            patient {
                ...patientFields
            }
            version
            availableVersions {
                version
                createdAt
            }
        }
    }
    ${patientFragment}
`;

export const deleteConstructionsFileTemplate = gql`
    mutation deleteConstructionsFile($fileId: MongoDbObjectId!, $orderId: MongoDbObjectId!) {
        deleteConstructionsFile(fileId: $fileId, orderId: $orderId) {
            ...internalOrderDetailsFields
        }
    }
    ${internalOrderDetailsFragment}
`;

export const reCreateDeliveryNoteTemplate = gql`
    mutation reCreateDeliveryNote($orderId: MongoDbObjectId!) {
        reCreateDeliveryNote(orderId: $orderId) {
            _id
        }
    }
`;

export const copyOrderTemplate = gql`
    mutation copyOrder($orderDbId: MongoDbObjectId!, $copyType: String!, $orderSection: OrderSection!) {
        copyOrder(orderDbId: $orderDbId, copyType: $copyType, orderSection: $orderSection) {
            _id
            isCreatedByLab
        }
    }
`;

export const modifyParticipantsTemplate = gql`
    mutation modifyParticipants($orderId: MongoDbObjectId!, $participants: Participants!) {
        modifyParticipants(orderId: $orderId, participants: $participants) {
            _id
            linkedDentists {
                user {
                    _id
                    fullName
                    email
                }
                dateAdded
            }
            linkedClinics {
                clinic {
                    _id
                    name
                    contact {
                        communication {
                            email
                        }
                    }
                }
                dateAdded
            }
            customParticipantEmails
        }
    }
`;

export const batchArchiveOrdersTemplate = gql`
    mutation batchArchiveOrders($orderIds: [MongoDbObjectId]!) {
        batchArchiveOrders(orderIds: $orderIds)
    }
`;

export const updateDeliveryDateTemplate = gql`
    mutation updateDeliveryDate($orderId: MongoDbObjectId!, $deliveryDate: Date!, $time: DeliveryTimeInput) {
        updateDeliveryDate(orderId: $orderId, deliveryDate: $deliveryDate, time: $time) {
            _id
            deliveryDetails {
                date
                time {
                    hour
                    minute
                }
            }
        }
    }
`;

export const saveCompletedDraftTemplate = gql`
    mutation saveCompletedDraft($orderId: MongoDbObjectId!) {
        saveCompletedDraft(orderId: $orderId) {
            _id
        }
    }
`;

export const saveOrderProductionValueReportTemplate = gql`
    mutation saveOrderProductionValueReport(
        $orderId: MongoDbObjectId!
        $productionValueReports: [InternalProductionValueReportInput!]!
    ) {
        saveOrderProductionValueReport(orderId: $orderId, productionValueReports: $productionValueReports)
    }
`;

export const updateReadyForInvoiceDateTemplate = gql`
    mutation updateReadyForInvoiceDate($orderId: MongoDbObjectId!, $date: Date!) {
        updateReadyForInvoiceDate(orderId: $orderId, date: $date) {
            _id
            invoiceActionTime
        }
    }
`;

export const updateProductionPlanTemplate = gql`
    mutation updateProductionPlan($orderId: MongoDbObjectId!, $productionPlan: [ProductionPlanStepInput!]!) {
        updateProductionPlan(orderId: $orderId, productionPlan: $productionPlan)
    }
`;

export const resendLabOrderByDentistTemplate = gql`
    mutation resendLabOrderByDentist($orderId: MongoDbObjectId!, $order: ResendLabOrderByDentistInput!) {
        resendLabOrderByDentist(orderId: $orderId, order: $order) {
            ...orderDetailsFields
        }
    }
    ${orderDetailsFragment}
`;

export const externalNotesTemplate = gql`
    mutation externalNotes($labId: MongoDbObjectId!, $orderId: MongoDbObjectId!, $externalNotes: String!) {
        externalNotes(labId: $labId, orderId: $orderId, externalNotes: $externalNotes) {
            ...internalOrderDetailsFields
        }
    }
    ${internalOrderDetailsFragment}
`;

// --- DD-2506 step 3: the one order write contract ----------------------------------------

export const createOrderTemplate = gql`
    mutation createOrder($order: OrderInput!, $draftId: MongoDbObjectId) {
        createOrder(order: $order, draftId: $draftId) {
            _id
            orderId
            patient {
                firstName
                lastName
            }
        }
    }
`;

// DD-3484 Marketplace: the client sends only ids and toggles; the server builds the
// sub-order from the parent order and the selection.
export const subcontractOrderTemplate = gql`
    mutation subcontractOrder($input: SubcontractOrderInput!) {
        subcontractOrder(input: $input) {
            _id
            orderId
        }
    }
`;

export const modifyOrderTemplate = gql`
    mutation modifyOrder($orderId: MongoDbObjectId!, $order: OrderInput!, $isResend: Boolean) {
        modifyOrder(orderId: $orderId, order: $order, isResend: $isResend) {
            _id
        }
    }
`;

export const saveOrderDraftTemplate = gql`
    mutation saveOrderDraft($orderId: MongoDbObjectId, $order: OrderDraftInput!) {
        saveOrderDraft(orderId: $orderId, order: $order) {
            _id
            isDraft
            status
        }
    }
`;

export const updateRestorationTemplate = gql`
    mutation updateRestoration($orderId: MongoDbObjectId!, $restoration: RestorationInput!) {
        updateRestoration(orderId: $orderId, restoration: $restoration) {
            ...orderDetailsFields
        }
    }
    ${orderDetailsFragment}
`;

export const addRestorationTemplate = gql`
    mutation addRestoration($orderId: MongoDbObjectId!, $restoration: RestorationInput!) {
        addRestoration(orderId: $orderId, restoration: $restoration) {
            ...orderDetailsFields
        }
    }
    ${orderDetailsFragment}
`;

export const removeRestorationTemplate = gql`
    mutation removeRestoration($orderId: MongoDbObjectId!, $restorationId: MongoDbObjectId!) {
        removeRestoration(orderId: $orderId, restorationId: $restorationId) {
            ...orderDetailsFields
        }
    }
    ${orderDetailsFragment}
`;

export const addRestorationMaterialsTemplate = gql`
    mutation addRestorationMaterials(
        $orderId: MongoDbObjectId!
        $restorationId: MongoDbObjectId!
        $materials: [RestorationMaterialInput!]!
    ) {
        addRestorationMaterials(orderId: $orderId, restorationId: $restorationId, materials: $materials) {
            ...orderDetailsFields
        }
    }
    ${orderDetailsFragment}
`;

export const removeRestorationRowTemplate = gql`
    mutation removeRestorationRow(
        $orderId: MongoDbObjectId!
        $restorationId: MongoDbObjectId!
        $rowId: MongoDbObjectId!
    ) {
        removeRestorationRow(orderId: $orderId, restorationId: $restorationId, rowId: $rowId) {
            ...orderDetailsFields
        }
    }
    ${orderDetailsFragment}
`;

// DD-3463: batch actions. One result shape for both mutations.
export const batchOrdersResultFragment = gql`
    fragment batchOrdersResultFields on BatchOrdersResult {
        deliveryNoteBatchId
        succeeded {
            _id
            orderId
        }
        failed {
            _id
            orderId
            message
        }
        skipped {
            _id
            orderId
            reason
        }
    }
`;

export const batchMarkOrdersProductionFinishedTemplate = gql`
    mutation batchMarkOrdersProductionFinished($orderIds: [MongoDbObjectId!]!, $compileDeliveryNotes: Boolean) {
        batchMarkOrdersProductionFinished(orderIds: $orderIds, compileDeliveryNotes: $compileDeliveryNotes) {
            ...batchOrdersResultFields
        }
    }
    ${batchOrdersResultFragment}
`;

export const batchSendOrdersTemplate = gql`
    mutation batchSendOrders($input: BatchSendOrdersInput!) {
        batchSendOrders(input: $input) {
            ...batchOrdersResultFields
        }
    }
    ${batchOrdersResultFragment}
`;

// DD-3391: the elevated external tier's batch send, matching their single-order one.
export const batchSendOrdersAsExternalUserTemplate = gql`
    mutation batchSendOrdersAsExternalUser($input: ExternalUserBatchSendOrdersInput!) {
        batchSendOrdersAsExternalUser(input: $input) {
            ...batchOrdersResultFields
        }
    }
    ${batchOrdersResultFragment}
`;

// DD-3592: regenerate the delivery notes of a batch's failed orders as a new
// batch. Order statuses are untouched; only the PDFs are rebuilt.
export const rerunDeliveryNoteBatchTemplate = gql`
    mutation rerunDeliveryNoteBatch($batchId: MongoDbObjectId!) {
        rerunDeliveryNoteBatch(batchId: $batchId) {
            deliveryNoteBatchId
            total
            skipped {
                _id
                orderId
            }
        }
    }
`;
